import React, { useEffect, useCallback, useRef, useMemo } from 'react';

import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import AIPromptInput from './AIPromptInput';
import UseCaseSelector from './UseCaseSelector';
import FilmStripSlider from './components/FilmStripSlider';
import LoadingState from './components/LoadingState';
import VideoPlayer from './components/VideoPlayer';
import {
  useVideoMetadata,
  useVideoPlayback,
  useVideoSelection,
  useVideoUI,
} from './context/VideoEditorContext';
import { useCases } from './utils/constants';
import { MAX_SELECTION_DURATION } from './utils/videoUtils';

interface VideoEditorUIProps {
  onGenerateVideo: () => void;
}

const VideoEditorUI: React.FC<VideoEditorUIProps> = ({ onGenerateVideo }) => {
  const currentTimeRef = useRef(0);

  const { videoSrc, videoMetadata, error, isLoadingMetadata } =
    useVideoMetadata();
  const { isPlaying, handlePlayPause, handleTimeUpdate } = useVideoPlayback();
  const { selection, handleSelectionChange } = useVideoSelection();
  const { selectedUseCase, defaultPlaceholder, handleUseCaseSelect } =
    useVideoUI();

  const currentSuggestions = useMemo(() => {
    if (!selectedUseCase) return [];
    const useCase = useCases.find((uc) => uc.id === selectedUseCase);
    return useCase ? useCase.suggestions : [];
  }, [selectedUseCase]);

  useEffect(() => {
    if (videoMetadata && !selection) {
      const initialSelection = {
        startTime: 0,
        endTime: Math.min(videoMetadata.duration, MAX_SELECTION_DURATION),
        duration: Math.min(videoMetadata.duration, MAX_SELECTION_DURATION),
      };
      handleSelectionChange(initialSelection);
    }
  }, [videoMetadata, selection, handleSelectionChange]);

  const handleVideoTimeUpdate = (time: number) => {
    currentTimeRef.current = time;
    handleTimeUpdate(time);
  };

  const handleGenerateClick = useCallback(() => {
    onGenerateVideo();
  }, [onGenerateVideo]);

  useEffect(() => {
    if (videoMetadata && videoSrc) {
      // Start playing automatically when the modal opens
      // The video will start from the selection start time due to VideoPlayer's selection logic
      const timer = setTimeout(() => {
        handlePlayPause();
      }, 500); // Small delay to ensure video is ready

      return () => clearTimeout(timer);
    }
  }, [videoMetadata, videoSrc, handlePlayPause]);

  if (isLoadingMetadata || error) {
    return (
      <LoadingState
        progress={50}
        message={error ? `Error: ${error}` : 'Loading video...'}
      />
    );
  }

  if (!videoSrc || !videoMetadata) {
    return null;
  }

  return (
    <>
      <div className="w-full flex flex-col mb-2">
        <Text fontSize="sm" className="text-white mb-2">
          Select footage:
        </Text>

        {/* Video Player and Filmstrip Container */}
        <div className="flex flex-col w-full">
          <VideoPlayer
            src={videoSrc}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onTimeUpdate={handleVideoTimeUpdate}
            currentTime={currentTimeRef.current}
            className="w-full"
          />

          {selection && (
            <FilmStripSlider
              selection={selection}
              onSelectionChange={handleSelectionChange}
              onVideoTimeUpdate={handleVideoTimeUpdate}
            />
          )}
        </div>
      </div>

      <UseCaseSelector
        useCases={useCases}
        selectedUseCase={selectedUseCase}
        onUseCaseSelect={(id) => handleUseCaseSelect(id, useCases)}
      />

      <AIPromptInput
        onGenerateVideo={handleGenerateClick}
        suggestions={currentSuggestions}
        defaultPlaceholder={defaultPlaceholder}
      />
    </>
  );
};

VideoEditorUI.displayName = 'VideoEditorUI';

export default VideoEditorUI;
